import { isHeadAdmin } from './permissions';

export const VIEW_ADMIN_PANEL = 'view_admin_panel';
export const HEAD_ADMIN_ROLE = 'site_owner';

export const ADMIN_PANEL_CHILD_PERMISSIONS = [
  'add_user',
  'edit_user',
  'delete_user',
  'manage_roles',
  'manage_lobbies',
];

export function sortRoles(roles) {
  return [...roles].sort((a, b) => {
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.role_id - b.role_id;
  });
}

export function getRoleRank(roles, roleId) {
  return sortRoles(roles).findIndex((r) => Number(r.role_id) === Number(roleId));
}

export function actorHasPermission(actorPermissions, permissionName, userRole) {
  if (isHeadAdmin(userRole) || actorPermissions.includes('all')) return true;
  return actorPermissions.includes(permissionName);
}

export function roleHasPermission(role, permissionName) {
  return (role.permissions || []).some((p) => p.permission_name === permissionName);
}

export function shouldForceViewAdminPanel(role) {
  return ADMIN_PANEL_CHILD_PERMISSIONS.some((name) => roleHasPermission(role, name));
}

export function canModifyRole(roles, actorRoleId, targetRole, userRole, canManageRoles) {
  if (!targetRole || targetRole.role_name === HEAD_ADMIN_ROLE) return false;
  if (isHeadAdmin(userRole)) return getRoleRank(roles, targetRole.role_id) > 0;
  if (!canManageRoles) return false;
  const actorRank = getRoleRank(roles, actorRoleId);
  const targetRank = getRoleRank(roles, targetRole.role_id);
  if (actorRank < 0 || targetRank < 0) return false;
  return targetRank > actorRank;
}

/** site_owner can upload a badge for the site_owner role only. */
export function canEditRoleBadge(roles, actorRoleId, targetRole, userRole, canManageRoles) {
  if (!targetRole) return false;
  if (targetRole.role_name === HEAD_ADMIN_ROLE) return isHeadAdmin(userRole);
  return canModifyRole(roles, actorRoleId, targetRole, userRole, canManageRoles);
}

export function canReorderRole(roles, actorRoleId, targetRole, userRole, canManageRoles) {
  return canModifyRole(roles, actorRoleId, targetRole, userRole, canManageRoles);
}

export function getAssignableRoles(roles, actorRoleId, userRole, canManageRoles) {
  return sortRoles(roles).filter((role) => canModifyRole(
    roles,
    actorRoleId,
    role,
    userRole,
    canManageRoles,
  ));
}

export function getValidReassignTargetsForDelete(
  roles,
  actorRoleId,
  roleToDelete,
  userRole,
  canManageRoles,
) {
  const deleteId = Number(roleToDelete.role_id);
  return getAssignableRoles(roles, actorRoleId, userRole, canManageRoles)
    .filter((r) => Number(r.role_id) !== deleteId);
}

export function canDeleteRole(
  roles,
  actorRoleId,
  targetRole,
  userCount,
  userRole,
  canManageRoles,
) {
  if (!canModifyRole(roles, actorRoleId, targetRole, userRole, canManageRoles)) {
    return { ok: false, message: 'Forbidden' };
  }
  const count = Number(userCount) || 0;
  if (count > 0) {
    const targets = getValidReassignTargetsForDelete(
      roles,
      actorRoleId,
      targetRole,
      userRole,
      canManageRoles,
    );
    if (targets.length === 0) {
      return {
        ok: false,
        message: 'Cannot delete this role: users would have nowhere valid to reassign (no roles below yours except this one)',
      };
    }
  }
  return { ok: true };
}

export function canTogglePermission(
  roles,
  actorRoleId,
  targetRole,
  permissionName,
  actorPermissions,
  userRole,
  canManageRoles,
) {
  if (targetRole.role_name === HEAD_ADMIN_ROLE) return false;
  if (!canModifyRole(roles, actorRoleId, targetRole, userRole, canManageRoles)) return false;
  if (permissionName === VIEW_ADMIN_PANEL && shouldForceViewAdminPanel(targetRole)) return false;
  return actorHasPermission(actorPermissions, permissionName, userRole);
}

export function isPermissionChecked(role, permissionName) {
  if (role.role_name === HEAD_ADMIN_ROLE) return true;
  if (permissionName === VIEW_ADMIN_PANEL && shouldForceViewAdminPanel(role)) return true;
  return roleHasPermission(role, permissionName);
}

/** Sentinel: drop past the last reorderable role */
export const ROLE_DROP_END = '__role_drop_end__';

export function reorderRolesList(roles, dragId, overId) {
  const sorted = sortRoles(roles);
  const from = sorted.findIndex((r) => Number(r.role_id) === Number(dragId));
  if (from < 0) return sorted;

  const next = [...sorted];
  const [moved] = next.splice(from, 1);

  if (overId === ROLE_DROP_END) {
    next.push(moved);
    return next;
  }

  const to = next.findIndex((r) => Number(r.role_id) === Number(overId));
  if (to < 0) return sorted;
  if (Number(dragId) === Number(overId)) return sorted;
  next.splice(to, 0, moved);
  return next;
}

export function formatRoleLabel(roleName) {
  if (!roleName) return '';
  if (roleName === HEAD_ADMIN_ROLE) return 'Site Owner';
  return roleName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}